import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHButton, FHInputField, FSwitch, FHTooltip } from '../../../../base';
import { behaviorActions, goalActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import LongTermGoalSection from './LongTermGoalSection';
import ShortTermGoalSection from './ShortTermGoalSection';
import DescriptionSection from './DescriptionSection';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS } from '../common/constants/general';
import { GoalTypes, ThresholdTypes } from './constants';
import SaveIcon from '../../../../svgs/SaveIcon';
import DeleteIcon from '../../../../svgs/DeleteIcon';

import type {
  ITreatmentPlanGoalTarget,
  ITreatmentPlanGoal,
  IBehaviorGoalName,
} from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface GoalItemProps {
  values: ITreatmentPlanGoal;
  goalType: GoalTypes;
  submitting?: boolean;
  targetSaving: boolean;
  errors?: any;
  onUpdateField: (key: string, value: any) => void;
  onSave: () => void;
  onDelete: () => void;
  onSaveTarget: (index: number, target: ITreatmentPlanGoalTarget) => void;
}

function GoalItem(props: GoalItemProps) {
  const {
    values: goal,
    goalType = GoalTypes.BehaviorReduction,
    submitting = false,
    targetSaving = false,
    errors,
    onUpdateField,
    onSave,
    onDelete,
    onSaveTarget,
  } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { skillAcquisitionGoals, caregiverGoals } = useSelector((state: any) => state.goals);
  const { goalNames } = useSelector((state: any) => state.behaviors);
  const [goalNameList, setGoalNameList] = useState<IBehaviorGoalName[]>([]);
  const goalRef = useRef(goal);
  const [activeStatus, setActiveStatus] = useState<boolean>(true);
  const [updatedActiveStatus, setUpdatedActiveStatus] = useState<boolean>(true);
  const [isReactivating, setIsReactivating] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    goalRef.current = goal;
  }, [goal]);

  useEffect(() => {
    const behaviorId = goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id;
    if (behaviorId == null) return;

    if (!goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) {
      onUpdateField(GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID, '');
    }

    if (goalNames && goalNames[behaviorId]) {
      setGoalNameList(goalNames[behaviorId]);
    } else {
      const fetchGoalNames = async () => {
        const { payload } = await dispatch(
          behaviorActions.getGoalNames({
            behaviorId,
            getAccessTokenSilently,
          }),
        );

        if (!payload) {
          return;
        }

        setGoalNameList(payload);
      };

      fetchGoalNames();
    }
  }, [goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id]);

  useEffect(() => {
    setActiveStatus(goal?.is_active);
    setUpdatedActiveStatus(goal?.is_active);
    setIsEditable(isTpEditable && goal?.is_active);
  }, [goal?.is_active, isTpEditable]);

  const handleAddTarget = () => {
    if (!goalRef) return;

    const goal = goalRef.current;
    onUpdateField('targets', [
      ...goal.targets,
      {
        label: '',
        comparand: 100,
        comparand_type:
          goalType === GoalTypes.BehaviorReduction ? ThresholdTypes.Number : ThresholdTypes.Percent,
        is_active: true,
      },
    ]);
  };

  const handleDeleteTarget = async (index: number) => {
    if (!treatmentPlan?.id || !goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    if (targets.length <= 1) {
      toast.error('Goal should have at least one target.');
      return;
    }

    if (Object.hasOwn(targets[index], 'id')) {
      await dispatch(
        goalActions.removeGoalTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          getAccessTokenSilently,
        }),
      );
    }

    onUpdateField(
      'targets',
      targets.filter((_, i) => i !== index),
    );
  };

  const handleSaveTarget = (index: number, key: string, value: string | number) => {
    if (!goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    const target = { ...targets[index], [key]: value };
    onSaveTarget(index, target);
  };

  const handleSwitchToggle = (checked: boolean) => {
    if (!treatmentPlan || !goal) return;

    const activeSkillAcquisitionGoals = skillAcquisitionGoals?.filter(
      (sg: ITreatmentPlanGoal) => sg.is_active && sg.id !== goal.id,
    );
    const activeCaregiverGoals = caregiverGoals?.filter(
      (cg: ITreatmentPlanGoal) => cg.is_active && cg.id !== goal.id,
    );
    if (!checked) {
      if (goalType === GoalTypes.SkillAcquisition && activeSkillAcquisitionGoals.length === 0) {
        toast.error('TP should have at least one active goal in skill acquisition.');
        return;
      }
      if (goalType === GoalTypes.Caregiver && activeCaregiverGoals.length === 0) {
        toast.error('TP should have at least one active goal in parent/caregiver.');
        return;
      }
    }

    setActiveStatus(checked);
    setReason('');
    // NOTE: isReactivating for displaying reason input field which was not rendered initially.
    setIsReactivating(true);
  };

  const handleUpdateGoalActivation = async () => {
    if (!treatmentPlan || !goal) return;
    if (activeStatus === updatedActiveStatus) return;

    if (reason.trim().length < 2) {
      toast.error('Reason requires at least 2 characters.');
      return;
    }

    if (!activeStatus) {
      const res = await dispatch(
        goalActions.deactivateGoal({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Goal deactivated.');
    } else {
      const res = await dispatch(
        goalActions.activateGoal({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Goal activated.');
    }

    onUpdateField('is_active', activeStatus);
  };

  const handleUpdateTargetActivation = async (index: number, reason: string) => {
    if (!treatmentPlan || !goal || !goal.targets || !Object.hasOwn(goal.targets[index], 'id'))
      return;

    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    if (reason.trim().length < 2) {
      toast.error('Reason requires at least 2 characters.');
      return;
    }

    if (targets[index].is_active) {
      const res = await dispatch(
        goalActions.deactivateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Target deactivated.');
    } else {
      const res = await dispatch(
        goalActions.activateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Target activated.');
    }

    onUpdateField(
      'targets',
      targets.map((target, i) =>
        index === i ? { ...target, is_active: !target.is_active } : target,
      ),
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        backgroundColor: FORTA_BLUE.primary,
        borderRadius: '10px',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        pt: 3,
        mt: 3,
        mb: 6,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          backgroundColor: FORTA_WHITE,
          borderRadius: '10px',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          mb={2}
        >
          <Typography fontWeight={'bold'}>
            Goal: {goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}
          </Typography>
          <FHTooltip
            title="Activate"
            followCursor
            disableFocusListener
            disableTouchListener
            placement="bottom-start"
          >
            <Box>
              <FSwitch
                data-testid={`goalItemActiveStatus${activeStatus}`}
                colorTheme={Themes.Primary}
                coloredBackground
                checked={activeStatus}
                disabled={!isTpEditable || !Object.hasOwn(goal, 'id')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleSwitchToggle(e.target.checked);
                }}
              />
            </Box>
          </FHTooltip>
        </Box>
        {isReactivating && (
          <Grid item xs={9} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography fontWeight={'bold'} textAlign={'left'} mr={2}>
              Reason for status change:*
            </Typography>
            <FHInputField
              sx={{ flex: 1 }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              coloredBackground
              value={reason}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              onBlur={() => {
                handleUpdateGoalActivation();
              }}
              error={reason?.length < 2 ? true : false}
            />
          </Grid>
        )}
        <LongTermGoalSection
          goal={goal}
          treatmentPlanId={treatmentPlan.id}
          goalType={goalType}
          errors={errors}
          onUpdateField={onUpdateField}
        />
        <Divider
          sx={{ height: 3, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '3px' }}
        />
        <ShortTermGoalSection
          goal={goal}
          goalNames={goalNameList}
          errors={errors}
          targetSaving={targetSaving}
          onUpdateField={onUpdateField}
          onAddTarget={() => {
            // Wait until update the targets when click save as soon as on blur the target label
            setTimeout(() => {
              handleAddTarget();
            }, 1000);
          }}
          onSaveTarget={handleSaveTarget}
          onDeleteTarget={handleDeleteTarget}
          onUpdateTargetActivation={handleUpdateTargetActivation}
        />
        <Divider
          sx={{ height: 3, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '3px' }}
        />
        <DescriptionSection goal={goal} errors={errors} onUpdateField={onUpdateField} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography fontWeight={'bold'}>Goal progress: 00%</Typography>
          <Box>
            {goal && (
              <FHButton
                data-testid="deleteGoalButton"
                startIcon={<DeleteIcon color={FORTA_WHITE} />}
                colorTheme={Themes.Primary}
                disabled={!isEditable || submitting}
                sx={{ mr: 2 }}
                onClick={onDelete}
              >
                Delete Goal
              </FHButton>
            )}
            <FHButton
              data-testid="saveGoaButton"
              startIcon={<SaveIcon />}
              colorTheme={Themes.Primary}
              disabled={!isEditable || submitting}
              onClick={() => onSave()}
              error={goal?.dirty}
            >
              Save Goal
            </FHButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default GoalItem;
